import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import {
  Button,
  List,
  ListItemProps,
  PillAction,
  Popper,
  Spinner,
  StatusDot,
  Tooltip
} from '@xsolla/ui-core';
import { AlertExclamationColorful, BlockCircle, ChevronRightMini, Cog } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import {
  deleteSocialConnections,
  disconnectPlatform,
  setPlatformConnection,
  setSocialConnections,
  SocialConnection,
  SocialConnectionsResponse
} from '../../../../api';
import { ChecklistItems } from '../../../../api/configurations/checklist/types';
import { Error as ErrorType } from '../../../../api/setup/getApiError';
import { Google } from '../../../../assets';
import { useConfig } from '../../../../contexts/config';
import { useChecklist } from '../../../../contexts/settings';
import { useNotifications } from '../../../../hooks/useNotifications';
import { usePaths } from '../../../../routes/routesHelper';
import { LinkIcon } from '../../../ui';
import BabkaDisconnectModal from '../BabkaDisconnectModal';
import { ChosenSocials } from '../index';
import { PlatformSettingsModal } from '../PlatformSettingsModal';
import { SocialIconsList } from './SocialIcons';
import {
  CardSpinner,
  SocialCardSubtitle,
  SocialCardTitle,
  SocialCardWrapper,
  SocialIconWrapper,
  SocialNetworkDescription,
  TopNavigationBarWrapper
} from './style';
import getTryConnectionLink from './utils';

type Props = RouteComponentProps & {
  projectId: string;
  socialData: SocialConnection;
  chosen?: boolean;
  platformCard?: boolean;
  setChosenSocials?: React.Dispatch<React.SetStateAction<ChosenSocials>>;
  setFilteredSocials?: React.Dispatch<React.SetStateAction<SocialConnectionsResponse>>;
  setSocials: React.Dispatch<React.SetStateAction<SocialConnectionsResponse>>;
};

const SocialNetworkCard = ({
  projectId,
  socialData,
  chosen,
  platformCard,
  setChosenSocials,
  setFilteredSocials,
  setSocials,
  history
}: Props): JSX.Element => {
  const { socialConnectionSettingsPath } = usePaths();
  const intl = useIntl();
  const { merchantId, locale, xsollaAnalytics } = useConfig();
  const { enableChecklistItem } = useChecklist(projectId);
  const { showErrorNotify } = useNotifications();
  const [fetching, setFetching] = useState<boolean>(false);

  const isXsollaKeys = useMemo(
    () =>
      socialData.configurable_fields[0].value === null &&
      socialData.configurable_fields[1]?.value === null,
    [socialData.configurable_fields]
  );

  const [onOpenPlatformModal, onClosePlatformModal] = useModal(
    () => (
      <PlatformSettingsModal
        projectId={projectId}
        provider={socialData.provider}
        onClose={onClosePlatformModal}
      />
    ),
    []
  );

  const isValuesRequired = socialData.configurable_fields.find((field) => field.required);

  const setUpdatedSocials = useCallback(() => {
    setSocials((prev) => {
      return prev.map((item) => {
        if (item.provider === socialData.provider) {
          const newConfigurableFields = item.connected
            ? item.configurable_fields.map((field) => {
                return { ...field, value: null };
              })
            : item.configurable_fields;

          return {
            ...item,
            connected: !item.connected,
            configurable_fields: newConfigurableFields
          };
        }

        return item;
      });
    });
    if (setFilteredSocials) {
      setFilteredSocials((prev) => {
        return prev.map((item) => {
          return item.provider === socialData.provider
            ? { ...item, connected: !item.connected }
            : item;
        });
      });
    }
  }, [socialData]);

  const clickHandle = async () => {
    setFetching(true);
    if (socialData.connected) {
      try {
        if (platformCard) {
          await disconnectPlatform({ projectId, provider: socialData.provider });
        } else {
          await deleteSocialConnections(projectId, socialData.provider);
        }
      } catch (err) {
        // Disconnecting can be refused by the backend — it returns 422 with
        // 003-102 when this provider is the project's last login method other
        // than passkey. Without this the request failed silently and the card
        // stayed stuck in its loading state.
        showErrorNotify(err as ErrorType);
        setFetching(false);
        return;
      }

      xsollaAnalytics.sendClick(
        `login_pa_module__${platformCard ? 'platform-settings' : 'social-connection'}__disconnect`,
        projectId,
        {
          social: socialData.provider
        }
      );

      setUpdatedSocials();
      setFetching(false);
      return;
    }
    // Connecting needs the same guard as disconnecting above. A rejected call
    // here used to leave the card spinning for good: nothing catches it, and the
    // response interceptor only rewrites 401/403 into an error object without
    // showing anything, so the publisher saw neither an error nor a usable card.
    if (platformCard) {
      try {
        await setPlatformConnection({
          projectId,
          provider: socialData.provider,
          values: {
            client_id: socialData.configurable_fields[0].value || null,
            client_secret: socialData.configurable_fields[1].value || null
          }
        });
      } catch (err) {
        showErrorNotify(err as ErrorType);
        setFetching(false);
        return;
      }

      xsollaAnalytics.sendClick(`login_pa_module__platform-settings__connect`, projectId, {
        social: socialData.provider,
        client_id: socialData.configurable_fields[0].value || null,
        client_secret: socialData.configurable_fields[1].value || null
      });
    } else {
      const values = {
        ...socialData.configurable_fields.reduce(
          (prevValue, field) => ({
            ...prevValue,
            [field.name]: null
          }),
          {}
        ),
        one_tap_login: { enabled: !!socialData.one_tap_login?.enabled }
      };

      try {
        await setSocialConnections({
          projectId,
          values,
          provider: socialData.provider
        });
      } catch (err) {
        showErrorNotify(err as ErrorType);
        setFetching(false);
        return;
      }

      try {
        await enableChecklistItem(ChecklistItems.CONNECTED_SOCIAL);
      } catch (err) {
        // Deliberately swallowed, and kept out of the block above. The provider
        // is connected by this point; the checklist is only a setup-progress
        // marker. Reporting a failure here would tell the publisher the
        // connection failed when it did not, and routing it through the catch
        // above would also skip the analytics call below.
      }

      xsollaAnalytics.sendClick(`login_pa_module__social-connection__connect`, projectId, {
        social: socialData.provider,
        ...values
      });
    }

    setFetching(false);
    setUpdatedSocials();
  };

  const [onOpenBabkaDisconnectModal, onCloseBabkaDisconnectModal] = useModal(
    () => (
      <BabkaDisconnectModal
        onClose={onCloseBabkaDisconnectModal}
        onConfirm={() => {
          onCloseBabkaDisconnectModal();
          clickHandle();
        }}
      />
    ),
    []
  );

  const getLink = useMemo(() => {
    return getTryConnectionLink({ projectId, locale, merchantId, socialName: socialData.provider });
  }, [socialData.provider]);

  const SocialCardSettings: ListItemProps[] = [
    ...(socialData.provider !== 'xsolla-id'
      ? [
          {
            children: intl.messages['social-connection.card.settings.settings'],
            onClick: platformCard
              ? onOpenPlatformModal
              : () =>
                  history.push({
                    pathname: socialConnectionSettingsPath(projectId, socialData.provider)
                  }),
            icon: Cog,
            'data-test-id': 'btn-social-settings'
          } as ListItemProps
        ]
      : [])
  ];

  if (!platformCard) {
    SocialCardSettings.push({
      children: intl.messages['social-connection.card.settings.try'],
      icon: ChevronRightMini,
      onClick: () => window.open(getLink, '_blank'),
      disabled: !socialData.connected,
      'data-test-id': 'btn-connect-try-connection'
    });
  }

  if (!isValuesRequired || socialData.connected) {
    SocialCardSettings.unshift({
      children:
        intl.messages[
          `social-connection.card.settings.${socialData.connected ? 'disconnect' : 'connect'}`
        ],
      onClick:
        socialData.connected && socialData.provider === 'babka'
          ? () => onOpenBabkaDisconnectModal()
          : () => clickHandle(),
      icon: socialData.connected ? BlockCircle : LinkIcon,
      'data-test-id': 'btn-connect-social'
    });
  }

  const cardClickHandle = useCallback(() => {
    if (
      !socialData ||
      !setChosenSocials ||
      socialData.configurable_fields.some((field) => field.required)
    ) {
      return;
    }
    setChosenSocials((prev) => ({ ...prev, [socialData?.provider]: chosen ? null : socialData }));
  }, [socialData, chosen]);

  return (
    <SocialCardWrapper chosen={chosen} platformCard={!!platformCard} onClick={cardClickHandle}>
      <TopNavigationBarWrapper>
        <StatusDot
          data-test-id={`social-${socialData?.provider}-status`}
          palette={socialData?.connected ? 'action' : 'muted'}>
          {
            intl.messages[
              `social-connection.card.status.${
                socialData?.connected ? 'connected' : 'disconnected'
              }`
            ]
          }
        </StatusDot>
        <Popper entry={<List items={SocialCardSettings} />}>
          {(referenceProps) => (
            <PillAction
              {...referenceProps}
              data-test-id={`btn-${socialData?.provider}-settings`}
              palette="brand">
              {fetching ? (
                <CardSpinner data-test-id={`fetching-${socialData?.provider}-settings`}>
                  <Spinner size={16} />
                </CardSpinner>
              ) : (
                <Cog size={24} />
              )}
            </PillAction>
          )}
        </Popper>
      </TopNavigationBarWrapper>
      <SocialNetworkDescription>
        <SocialIconWrapper>{SocialIconsList[socialData?.provider] || <Google />}</SocialIconWrapper>
        <div>
          <SocialCardTitle>
            {intl.messages[`auth-methods.${socialData?.provider}`]}
            {socialData.connected && isXsollaKeys && socialData.provider !== 'xsolla-id' && (
              <Tooltip content={intl.messages['social-connection.card.tooltip']}>
                <Button data-test-id="social-dev-keys" look="icon" palette="brand">
                  <AlertExclamationColorful size={16} />
                </Button>
              </Tooltip>
            )}
          </SocialCardTitle>
          <SocialCardSubtitle>
            {intl.messages['social-connection.card.subscription.subtitle']}
          </SocialCardSubtitle>
        </div>
      </SocialNetworkDescription>
    </SocialCardWrapper>
  );
};

export default withRouter(SocialNetworkCard);
